// Faction goal / win-condition layer.
//
// The single biggest comprehension gap in the audit was that nothing ever told
// a first-timer their SIDE or HOW they win. The data already exists on every
// RoleConfig (`faction` + `victory`), so this module derives a localized
// "side + how you win" pair keyed on `victory.type`. It is reused at the role
// reveal, in the Codex, on the player lobby onboarding, and on GameOver — build
// it once, render it everywhere.
//
// NOTE: the goal is the role's STARTING goal. A few roles can change goal at
// runtime (e.g. executioner -> jester on `executioner-converted-to-jester`);
// that override is conveyed through the knowledge log, not this static map.

import type { Faction, Lang, RoleConfig, VictoryRule } from './types.js';
import { FACTION_LABEL } from './knowledge.js';

// Win condition phrased for a player, per `victory.type` (all 9 variants).
export const GOAL_TEXT: Record<VictoryRule['type'], Record<Lang, string>> = {
  town: {
    ru: 'Устранить всю мафию и убийц-одиночек.',
    uk: 'Усунути всю мафію та вбивць-одинаків.',
  },
  mafia: {
    ru: 'Сравняться числом с городом, убрав остальных.',
    uk: 'Зрівнятися числом з містом, прибравши решту.',
  },
  'solo-last-standing': {
    ru: 'Остаться последним, кто выжил.',
    uk: 'Лишитися останнім, хто вижив.',
  },
  'solo-burn-all-doused': {
    ru: 'Облить и сжечь всех живых игроков.',
    uk: 'Облити та спалити всіх живих гравців.',
  },
  'solo-voted-out': {
    ru: 'Добиться, чтобы вас казнили на дневном голосовании.',
    uk: 'Домогтися, щоб вас стратили на денному голосуванні.',
  },
  'solo-target-voted': {
    ru: 'Добиться, чтобы вашу цель казнили на голосовании.',
    uk: 'Домогтися, щоб вашу ціль стратили на голосуванні.',
  },
  'solo-all-infected': {
    ru: 'Заразить всех остальных живых игроков.',
    uk: 'Заразити всіх інших живих гравців.',
  },
  'solo-cult-parity': {
    ru: 'Привести культ к большинству среди живых.',
    uk: 'Привести культ до більшості серед живих.',
  },
  survive: {
    ru: 'Просто дожить до конца игры.',
    uk: 'Просто дожити до кінця гри.',
  },
};

// Short word for the "Цель" / goal label heading, per language.
export const GOAL_LABEL: Record<Lang, string> = {
  ru: 'ЦЕЛЬ',
  uk: 'МЕТА',
};

export interface RoleGoal {
  /** The mechanical faction ('town' | 'mafia' | 'solo'). */
  side: Faction;
  /** Localized side label (МИРНЫЕ / МАФИЯ / ОДИНОЧКА …). */
  sideLabel: string;
  /** Localized one-line win condition. */
  how: string;
}

/** Localized side label for a faction. */
export function factionLabel(faction: Faction, t: Lang): string {
  return FACTION_LABEL[faction][t];
}

/** Derive the side + win-condition pair for a role in the active language. */
export function goalForRole(role: RoleConfig, t: Lang): RoleGoal {
  return {
    side: role.faction,
    sideLabel: FACTION_LABEL[role.faction][t],
    how: GOAL_TEXT[role.victory.type][t],
  };
}
